import type { PrintTarget } from '@/components/sklad/PrintTargetModal';
import type { StriharnaProduct } from '@/components/vyroba/types';
import { apiFetch } from '@/lib/apiFetch';

/**
 * Naposledy zvolený cíl tisku plánu — vlastní klíč, ať se nemíchá s volbou
 * Zebra/klasický tisk pro štítky rolí (jiná tiskárna, jiný kontext).
 */
export function tiskneDoFrontyA4(): boolean {
  return localStorage.getItem('strihani-a4-tisk-fronta') === '1';
}

export function zapamatujCilTiskuA4(doFronty: boolean): void {
  localStorage.setItem('strihani-a4-tisk-fronta', doFronty ? '1' : '0');
}

// Tisk výsledků hledání podle látky na A4 — střihárna to dřív přepisovala
// ručně na papír, aby věděla, co si vzít z role naráz. Nahoře karty přesně
// jako na obrazovce (termín po termínu), dole SOUHRN — každý produkt jednou,
// se součtem kusů ze všech termínů dohromady.
//
// `dialog` jede přes HTML + window.print() (stejný princip jako štítky role
// v labelsPdf.ts). `fronta`/`pdf` jedou přes pdfmake — HTML tisk sám o sobě
// tablet na dílenskou tiskárnu (Xerox) neposílá, na to je potřeba fronta
// stejná jako u Zebry, jen s vlastním klíčem `xerox` a skutečným PDF souborem.

const WEEKDAYS = ['Neděle', 'Pondělí', 'Úterý', 'Středa', 'Čtvrtek', 'Pátek', 'Sobota'];

function fmtFull(iso: string): string {
  const [y, m, d] = iso.split('-');
  if (!y || !m || !d) return iso;
  const wd = WEEKDAYS[new Date(Number(y), Number(m) - 1, Number(d)).getDay()] ?? '';
  return `${wd} ${Number(d)}.${Number(m)}.${y}`;
}

function esc(s: string): string {
  return s
    .replace(/&/g, '&amp;')
    .replace(/</g, '&lt;')
    .replace(/>/g, '&gt;')
    .replace(/"/g, '&quot;')
    .replace(/'/g, '&#39;');
}

type Velikost = { variant_name: string | null; qty: number };
type Naplet = { barva: string; bg: string; fg: string } | null;
type Karta = {
  product_code: string;
  product_name: string;
  image_url: string | null;
  naplet: Naplet;
  variants: Velikost[];
  total_qty: number;
};

/** Souhrn — stejný produkt sečtený přes všechny termíny (podle velikosti). */
function aggregateSouhrn(groups: { term_date: string; products: StriharnaProduct[] }[]): Karta[] {
  const souhrn = new Map<
    string,
    { product_code: string; product_name: string; image_url: string | null; naplet: Naplet } & {
      bySize: Map<string, Velikost>;
    }
  >();
  for (const g of groups) {
    for (const p of g.products) {
      let entry = souhrn.get(p.product_code);
      if (!entry) {
        entry = {
          product_code: p.product_code,
          product_name: p.product_name,
          image_url: p.image_url,
          naplet: p.naplet,
          bySize: new Map(),
        };
        souhrn.set(p.product_code, entry);
      }
      for (const v of p.variants) {
        const key = v.variant_name ?? '';
        const existing = entry.bySize.get(key);
        entry.bySize.set(key, { variant_name: v.variant_name, qty: (existing?.qty ?? 0) + v.qty });
      }
    }
  }
  return [...souhrn.values()].map((p) => {
    // Mapa drží pořadí prvního výskytu napříč termíny — to nemusí sedět s
    // pořadím velikostí. Řadíme podle čísla ve štítku (98, 104, 110, …).
    const variants = [...p.bySize.values()].sort((a, b) => {
      const na = Number.parseInt(a.variant_name ?? '', 10);
      const nb = Number.parseInt(b.variant_name ?? '', 10);
      if (Number.isNaN(na) && Number.isNaN(nb)) return 0;
      if (Number.isNaN(na)) return 1;
      if (Number.isNaN(nb)) return -1;
      return na - nb;
    });
    return {
      product_code: p.product_code,
      product_name: p.product_name,
      image_url: p.image_url,
      naplet: p.naplet,
      variants,
      total_qty: variants.reduce((s, v) => s + v.qty, 0),
    };
  });
}

// ─── Běžný tisk (HTML + window.print) ──────────────────────────────────────

function velikostiTableHtml(variants: Velikost[]): string {
  const radky = variants
    .map(
      (v) =>
        `<tr><td>${esc(v.variant_name ?? 'Bez velikosti')}</td><td class="qty">${v.qty} ks</td></tr>`,
    )
    .join('');
  return `<table class="velikosti">${radky}</table>`;
}

function productCardHtml(p: Karta): string {
  return `<div class="card">
    <div class="thumb">${p.image_url ? `<img src="${esc(p.image_url)}" alt="" />` : ''}</div>
    <div class="info">
      <div class="code">${esc(p.product_code)}</div>
      <div class="name">${esc(p.product_name)}</div>
      ${
        p.naplet
          ? `<div class="naplet"><span class="label">Náplet:</span> <span class="badge" style="background:${esc(p.naplet.bg)};color:${esc(p.naplet.fg)}">${esc(p.naplet.barva)}</span></div>`
          : ''
      }
      ${velikostiTableHtml(p.variants)}
      <div class="total">Celkem ${p.total_qty} ks</div>
    </div>
  </div>`;
}

const CSS = `
  @page { size: A4; margin: 12mm; }
  * { box-sizing: border-box; }
  body { margin: 0; font-family: -apple-system, 'Segoe UI', Roboto, Helvetica, Arial, sans-serif; color: #000; }
  h1 { font-size: 16pt; margin: 0 0 2mm; }
  .needle { color: #555; font-size: 10pt; margin: 0 0 6mm; }
  h2 { font-size: 13pt; margin: 6mm 0 3mm; }
  .grid { display: grid; grid-template-columns: 1fr 1fr 1fr; gap: 3mm; margin-bottom: 2mm; }
  .card { display: flex; gap: 2.5mm; border: 0.3mm solid #ccc; border-radius: 2mm; padding: 2.5mm; break-inside: avoid; }
  .thumb { width: 15mm; height: 15mm; flex-shrink: 0; border: 0.2mm solid #ddd; overflow: hidden; }
  .thumb img { width: 100%; height: 100%; object-fit: cover; }
  .info { flex: 1; min-width: 0; }
  .code { font-family: monospace; font-size: 10.5pt; color: #666; }
  .name { font-size: 11.5pt; font-weight: 700; margin-bottom: 1mm; }
  .naplet { font-size: 9.5pt; margin-bottom: 1mm; }
  .naplet .label { font-weight: 700; }
  .badge { display: inline-block; border-radius: 3mm; padding: 0.3mm 2mm; font-size: 9pt; font-weight: 700; }
  table.velikosti { width: 100%; border-collapse: collapse; font-size: 11pt; }
  table.velikosti td { padding: 0.5mm 0; border-bottom: 0.2mm solid #eee; }
  table.velikosti td.qty { text-align: right; font-weight: 700; }
  .total { font-size: 8.5pt; color: #666; margin-top: 1mm; }
  .divider { border: none; border-top: 1mm solid #000; margin: 8mm 0 4mm; }
  .souhrn-title { font-size: 16pt; font-weight: 700; letter-spacing: 2px; margin: 0 0 4mm; }
`;

function tiskDialog(
  groups: { term_date: string; products: StriharnaProduct[] }[],
  needle: string,
): void {
  const detail = groups
    .map(
      (g) =>
        `<h2>${esc(fmtFull(g.term_date))}</h2><div class="grid">${g.products
          .map((p) =>
            productCardHtml({
              product_code: p.product_code,
              product_name: p.product_name,
              image_url: p.image_url,
              naplet: p.naplet,
              variants: p.variants,
              total_qty: p.total_qty,
            }),
          )
          .join('')}</div>`,
    )
    .join('');

  const souhrnHtml = `<div class="grid">${aggregateSouhrn(groups)
    .map((p) => productCardHtml(p))
    .join('')}</div>`;

  const win = window.open('', '_blank');
  if (!win) return;
  win.document.write(`<!DOCTYPE html><html><head><title>Plán stříhání — ${esc(
    needle,
  )}</title><style>${CSS}</style></head><body>
    <h1>Plán stříhání — látka „${esc(needle)}"</h1>
    <p class="needle">Vytištěno ${esc(fmtFull(new Date().toISOString().slice(0, 10)))}</p>
    ${detail}
    <hr class="divider" />
    <div class="souhrn-title">SOUHRN</div>
    ${souhrnHtml}
  </body></html>`);
  win.document.close();
  setTimeout(() => {
    win.focus();
    win.print();
  }, 500);
}

// ─── Fronta na dílenskou tiskárnu / stažení PDF (pdfmake) ──────────────────

type Content = Record<string, unknown>;

/** Minimální tvar pdfmake, který používáme (knihovna vlastní typy nenabízí v ESM). */
type PdfMake = {
  addVirtualFileSystem: (vfs: Record<string, string>) => void;
  createPdf: (def: Record<string, unknown>) => {
    download: (name: string) => void;
    getBlob: (cb: (blob: Blob) => void) => void;
  };
};

const IMG_MAX_PX = 200;

function loadImgEl(url: string): Promise<HTMLImageElement | null> {
  return new Promise((resolve) => {
    const img = new Image();
    img.crossOrigin = 'anonymous';
    img.onload = () => resolve(img);
    img.onerror = () => resolve(null);
    img.src = url;
  });
}

/** Fotka → malý JPEG data URL, ať pdfmake nemusí nic dotahovat samo. */
async function fetchDataUrl(url: string): Promise<string | null> {
  if (!url) return null;
  const img = await loadImgEl(url);
  if (!img) return null;
  const pw = img.naturalWidth || 1;
  const ph = img.naturalHeight || 1;
  const scale = Math.min(1, IMG_MAX_PX / Math.max(pw, ph));
  const w = Math.max(1, Math.round(pw * scale));
  const h = Math.max(1, Math.round(ph * scale));
  const canvas = document.createElement('canvas');
  canvas.width = w;
  canvas.height = h;
  const ctx = canvas.getContext('2d');
  if (!ctx) return null;
  ctx.drawImage(img, 0, 0, w, h);
  try {
    return canvas.toDataURL('image/jpeg', 0.85);
  } catch {
    // Cizí doména bez CORS → canvas je „tainted", karta bude bez fotky.
    return null;
  }
}

function pdfCard(p: Karta, photo: string | null): Content {
  return {
    columns: [
      photo
        ? { image: photo, width: 32, height: 32, fit: [32, 32] }
        : {
            canvas: [{ type: 'rect', x: 0, y: 0, w: 32, h: 32, lineColor: '#dddddd' }],
          },
      {
        width: '*',
        stack: [
          { text: p.product_code, fontSize: 9, color: '#666666' },
          { text: p.product_name, fontSize: 10, bold: true, margin: [0, 0, 0, 1] },
          ...(p.naplet
            ? [
                {
                  text: [{ text: 'Náplet: ', bold: true }, p.naplet.barva],
                  fontSize: 8.5,
                  margin: [0, 0, 0, 1],
                },
              ]
            : []),
          {
            table: {
              widths: ['*', 'auto'],
              body: p.variants.map((v) => [
                { text: v.variant_name ?? 'Bez velikosti', fontSize: 10, margin: [0, 1, 0, 1] },
                {
                  text: `${v.qty} ks`,
                  fontSize: 10,
                  bold: true,
                  alignment: 'right',
                  margin: [0, 1, 0, 1],
                },
              ]),
            },
            layout: 'lightHorizontalLines',
          },
          {
            text: `Celkem ${p.total_qty} ks`,
            fontSize: 7.5,
            color: '#666666',
            margin: [0, 1, 0, 0],
          },
        ],
      },
    ],
    columnGap: 6,
  };
}

/** Karty po třech vedle sebe — pdfmake nemá vlastní „auto-wrap" grid. */
function cardGrid(cards: Content[]): Content[] {
  const perRow = 3;
  const rows: Content[] = [];
  for (let i = 0; i < cards.length; i += perRow) {
    const radek = cards.slice(i, i + perRow);
    while (radek.length < perRow) radek.push({});
    rows.push({
      columns: radek,
      columnGap: 8,
      margin: [0, 0, 0, 6],
    });
  }
  return rows;
}

/** pdfmake s písmy — načte se až při tisku (velký balík). */
export async function nactiPdfMake(): Promise<PdfMake> {
  const [pdfMakeMod, vfsMod] = await Promise.all([
    import('pdfmake/build/pdfmake'),
    import('pdfmake/build/vfs_fonts'),
  ]);
  const pdfMake = ((pdfMakeMod as unknown as { default?: PdfMake }).default ??
    pdfMakeMod) as PdfMake;
  const vfs = ((vfsMod as unknown as { default?: Record<string, string> }).default ??
    vfsMod) as Record<string, string>;
  pdfMake.addVirtualFileSystem(vfs);
  return pdfMake;
}

async function buildPdfMakeDoc(
  groups: { term_date: string; products: StriharnaProduct[] }[],
  needle: string,
) {
  const pdfMake = await nactiPdfMake();

  // Fotky se stahují jednou za URL, ať se stejná neopakuje pro detail i souhrn.
  const urls = new Set<string>();
  for (const g of groups) for (const p of g.products) if (p.image_url) urls.add(p.image_url);
  const photoEntries = await Promise.all(
    [...urls].map(async (u) => [u, await fetchDataUrl(u)] as const),
  );
  const photos = new Map(photoEntries);

  const content: Content[] = [
    { text: 'PLÁN STŘÍHÁNÍ', fontSize: 18, bold: true, alignment: 'center', margin: [0, 0, 0, 2] },
    {
      text: `Látka „${needle}"`,
      fontSize: 12,
      alignment: 'center',
      color: '#555555',
      margin: [0, 0, 0, 2],
    },
    {
      text: `Vytištěno ${fmtFull(new Date().toISOString().slice(0, 10))}`,
      fontSize: 8,
      alignment: 'center',
      color: '#888888',
      margin: [0, 0, 0, 14],
    },
  ];

  for (const g of groups) {
    content.push({ text: fmtFull(g.term_date), fontSize: 13, bold: true, margin: [0, 0, 0, 6] });
    content.push(
      ...cardGrid(
        g.products.map((p) =>
          pdfCard(
            {
              product_code: p.product_code,
              product_name: p.product_name,
              image_url: p.image_url,
              naplet: p.naplet,
              variants: p.variants,
              total_qty: p.total_qty,
            },
            p.image_url ? (photos.get(p.image_url) ?? null) : null,
          ),
        ),
      ),
    );
  }

  // Tlustá čára místo lámání na novou stránku — často se to i tak vejde na
  // jednu A4 a zbytečně by se plýtvalo papírem.
  content.push({
    canvas: [{ type: 'line', x1: 0, y1: 0, x2: 535, y2: 0, lineWidth: 3 }],
    margin: [0, 10, 0, 10],
  });
  content.push({
    text: 'SOUHRN',
    fontSize: 16,
    bold: true,
    alignment: 'center',
    margin: [0, 0, 0, 10],
  });
  content.push(
    ...cardGrid(
      aggregateSouhrn(groups).map((p) =>
        pdfCard(p, p.image_url ? (photos.get(p.image_url) ?? null) : null),
      ),
    ),
  );

  return pdfMake.createPdf({
    pageSize: 'A4',
    pageMargins: [30, 30, 30, 30],
    defaultStyle: { font: 'Roboto', fontSize: 9 },
    content,
  });
}

/**
 * Vytiskne (na A4) výsledky hledání podle látky — nahoře stejný pohled jako
 * na obrazovce (termín po termínu), dole SOUHRN se sečtenými kusy napříč
 * termíny, ať si střihárna nemusí nic přepisovat ručně.
 *
 * `dialog` (výchozí) = tiskový dialog tohoto zařízení (HTML). `fronta` =
 * pošle se do tiskové fronty na dílenskou tiskárnu (Xerox) — pro tablet, na
 * kterou přímo tisknout neumí. `pdf` = jen stáhnout ke kontrole.
 */
export async function tiskStrihaniA4(
  groups: { term_date: string; products: StriharnaProduct[] }[],
  needle: string,
  target: PrintTarget = 'dialog',
): Promise<void> {
  if (target === 'dialog') {
    tiskDialog(groups, needle);
    return;
  }

  const doc = await buildPdfMakeDoc(groups, needle);
  const safe = (needle || 'latka').replace(/[^\p{L}\p{N}._-]+/gu, '_');

  if (target === 'pdf') {
    doc.download(`plan-strihani-${safe}.pdf`);
    return;
  }

  // fronta — stejný princip jako Zebra u štítků, jen vlastní klíč tiskárny.
  const blob = await new Promise<Blob>((resolve) => doc.getBlob(resolve));
  await apiFetch(
    `/api/print?title=${encodeURIComponent(`Plán stříhání — ${needle}`)}&printer=xerox`,
    {
      method: 'POST',
      headers: { 'Content-Type': 'application/pdf' },
      body: blob,
    },
  );
}
